import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MdCheck, MdClose, MdOpenInNew } from 'react-icons/md';

// Briques partagées par les deux fiches détail (propfirm & broker).

/** Bloc de section titré (icône + libellé majuscule) du drawer de détail. */
export function DetailSection({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2.5">
      <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
        {icon}
        <span>{title}</span>
      </h3>
      {children}
    </section>
  );
}

/** Carte libellé / valeur (règles, conditions). `wide` occupe toute la largeur. */
export function DetailCard({
  label,
  children,
  wide,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-md border border-white/[0.05] bg-white/[0.02] px-2.5 py-2',
        wide && 'col-span-full',
      )}
    >
      <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-500 md:text-[10px]">
        {label}
      </span>
      <span className="text-[12px] text-neutral-200">{children}</span>
    </div>
  );
}

/** Rend un booléen factuel : oui (coche) / non (croix) / inconnu (tiret). */
export function BoolValue({ value }: { value: boolean | null }) {
  const { t } = useTranslation('propfirms');
  if (value == null) return <span className="text-neutral-500">{t('value.none')}</span>;
  return value ? (
    <span className="inline-flex items-center gap-1 text-green-400">
      <MdCheck size={14} />
      {t('detail.yes')}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-neutral-400">
      <MdClose size={14} />
      {t('detail.no')}
    </span>
  );
}

function isoToLocale(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

function sourceLabel(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

/** Notes + liens de sources (ouverture externe neutre) + date de vérification. */
export function SourcesFooter({
  notes,
  sources,
  lastVerified,
}: {
  notes: string;
  sources: readonly string[];
  lastVerified: string;
}) {
  const { t } = useTranslation('propfirms');
  return (
    <div className="flex flex-col gap-3 border-t border-white/[0.06] pt-4">
      {notes && <p className="text-[11px] leading-relaxed text-neutral-400">{notes}</p>}
      {sources.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-500 md:text-[10px]">
            {t('detail.sources')}
          </span>
          <div className="flex flex-col gap-0.5">
            {sources.map((url) => (
              <button
                key={url}
                type="button"
                onClick={() => window.api.openExternal(url)}
                className="flex min-w-0 max-w-full items-center gap-1 self-start text-left text-[11px] text-neutral-500 transition-colors hover:text-primary-300 max-md:min-h-11"
              >
                <MdOpenInNew size={11} className="shrink-0" />
                <span className="min-w-0 truncate">{sourceLabel(url)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <span className="text-[11px] text-neutral-600 md:text-[10px]">
        {t('detail.lastVerified', { date: isoToLocale(lastVerified) })}
      </span>
    </div>
  );
}
